import {
  ApiHttpError,
  isAdsPlatform,
  type AdAccountSummary,
  type AdsOAuthIdentity,
  type AdsPlatform,
  type DiscoveredAccount,
} from '@/types';

import type { AccountSyncFact } from './accountSyncFacts';
import { getAdsErrorSlug } from './errorMap';
import { reauthRuledOut, deriveAccountActionState } from './syncViewModel';

/**
 * 廣告平台的純判準層(R299 Meta;BE docs/109)
 * =============================================================================
 * Google 與 Meta 的 Stage 1 端點**同形**,所以 service 層不分支(`adsService.forPlatform`);
 * 真正不同的是幾件「平台事實」,全部收在這一支、且用 `Record<AdsPlatform, …>` 逼窮舉 ——
 * 加第三個平台時 tsc 會逐表要求補值,不會有某張表靜默漏掉。
 *
 * 1. **帳戶 ID 的顯示格式**(F109-9):Google 的 `customer_id` 慣例 `xxx-xxx-xxxx`,
 *    Meta 的 FB `account_id` 是純數字、**不可套連字號**(也不含 `act_`)。
 * 2. **OAuth 起點路徑**:`/api/ads/{platform}/oauth/start[?reauth=]`(BE 兩邊同構)。
 * 3. **「平台現在沒有」的判準**(F109-7):Meta 端點在 `meta_ads` flag 關著時 403
 *    `FEATURE_DISABLED`;BE 那批未併主線(beta / prod)時是 404 —— 兩者對 FE 都是
 *    「入口藏起來」,不是錯誤 toast。⚠️ 業務 404(`IDENTITY_NOT_FOUND` /
 *    `DISCOVERY_NOT_FOUND`)**不算** —— 那是端點在、資源不在。
 * 4. **唯讀帳戶**(F109-4 / F109-5):Meta 的 `user_tasks` 含 `ADVERTISE` 或 `MANAGE`
 *    才可寫;帳戶列直接讀 BE 推導好的 `write_ok`,**未知(null)不畫唯讀 badge** ——
 *    對 Google 列與缺欄一律 null,標成唯讀會讓可寫的帳戶看起來不能上傳。
 * 5. **身分到期**(F109-6):只有 Meta 有 `expires_at`(長期 token ~60 天、無 refresh),
 *    剩 <7 天提前提示;Google 恆 null → 不提示。
 * 6. **連線狀態**(D3 廣告串接 的「連線狀態」欄):把授權層 / 平台端狀態 / 到期三件事
 *    收成一個 discriminated union,列表與抽屜共用同一個答案。
 */

/** 平台級顯示常數。`letter` = 品牌方塊上的字母(D3 `.acct-ic.g` / `.acct-ic.m`)。 */
export const ADS_PLATFORM_LETTER: Record<AdsPlatform, string> = {
  google: 'G',
  meta: 'M',
};

/** 各平台 discovery 列「啟用中」的 wire 值(Google `customer_client.status` / Meta `account_status` 標籤)。 */
export const DISCOVERED_ACTIVE_STATUS: Record<AdsPlatform, string> = {
  google: 'ENABLED',
  meta: 'ACTIVE',
};

/** Meta 可寫的 `user_tasks` 值(BE `capability.write_tasks`:ADVERTISE / MANAGE)。 */
export const META_WRITE_TASKS: readonly string[] = ['ADVERTISE', 'MANAGE'];

/** 長期 token 剩不到這麼多天要提前提示重新授權(docs/109 §5;BE `EXPIRY_WARN_DAYS` 同值)。 */
export const IDENTITY_EXPIRY_WARN_DAYS = 7;

const DAY_MS = 86_400_000;

/**
 * `?provider=` query(BE 302 回 FE 時帶;docs/109 §1 / §4)→ 平台。
 * 缺 / 認不得 → `google`:舊入口(R289 之前的連結、匯入精靈的「前往連結帳號」)都沒有這個
 * 參數,而它們全是 Google 線。
 */
export function parseProviderParam(v: string | null | undefined): AdsPlatform {
  return isAdsPlatform(v) ? v : 'google';
}

/**
 * 帳戶 ID 的顯示格式(F109-9)。
 *
 * - Google:10 位純數字 → `xxx-xxx-xxxx`(Google Ads UI 慣例;搜尋 / 貼上都吃這個形狀);
 *   不是 10 位(理論上不會)→ 原樣。
 * - Meta:**原樣**。FB `account_id` 長度不固定(6–20 位),套 Google 的分段會造出一個
 *   Meta 後台找不到的字串。
 */
export function formatCustomerId(platform: AdsPlatform, customerId: string): string {
  if (platform === 'google' && /^\d{10}$/.test(customerId)) {
    return `${customerId.slice(0, 3)}-${customerId.slice(3, 6)}-${customerId.slice(6)}`;
  }
  return customerId;
}

/**
 * 組某平台的 `/oauth/start` 導覽路徑(BE `oauth_service.OAUTH_START_PATH` 兩邊同構)。
 *
 * `?reauth=<oauth_user_id>` = 重新授權**這一把**(Google:`login_hint` 預選;Meta:
 * `auth_type=rerequest` 強制再出授權畫面)。`encodeURIComponent` 不是形式主義:id 來自
 * wire,不編碼的話一個 `&` 就能再塞一個 query 參數。
 *
 * ⚠️ 這個值會餵給 `window.location.replace()`;呼叫端一律再過 `isSameOriginPath` 守門。
 */
export function platformOAuthStartPath(
  platform: AdsPlatform,
  reauthOauthUserId?: string | null,
): string {
  const base = `/api/ads/${platform}/oauth/start`;
  if (!reauthOauthUserId) return base;
  return `${base}?reauth=${encodeURIComponent(reauthOauthUserId)}`;
}

/**
 * 把 `?reauth=<value>` 掛到 BE 給的 `authorization_url` 上(R300;docs/110 §1 規則 3)。
 *
 * 規則 1 要求「先 `POST /oauth/link`,只有 `NEEDS_AUTHORIZATION` 才導頁」,所以導頁用的是
 * link 回的 `authorization_url`(相對路徑,同 {@link platformOAuthStartPath} 的值);規則 3 又要
 * 重授權時帶 `?reauth=` —— 這支把兩件事接起來。已有 query 就用 `&`;value 一律 encode。
 * `reauth=1` 是 Meta `scope_missing` 重試的約定值(任意非空即「重授權意圖」,BE 會記進 state)。
 */
export function withReauthParam(authorizationUrl: string, reauth: string): string {
  const sep = authorizationUrl.includes('?') ? '&' : '?';
  return `${authorizationUrl}${sep}reauth=${encodeURIComponent(reauth)}`;
}

/**
 * 「這個平台現在沒有」(F109-7):Meta 入口要**藏起來**,不是報錯。
 *
 * - 403 `FEATURE_DISABLED` —— `meta_ads` flag 關著(BE `require_meta_ads_enabled`)。
 *   ⚠️ 那顆 `Error` 的 `message` 是人話(`This AI feature is currently disabled…`),
 *   slug 在 `code` 上 —— `getAdsErrorSlug` 兩處都讀,這裡不用自己拆。
 * - 404 **且不是業務 slug** —— 路由不存在(BE 那批未併主線;FastAPI 對不存在的路徑回
 *   `{"detail":"Not Found"}`,沒有 SCREAMING_SNAKE slug)。`IDENTITY_NOT_FOUND` /
 *   `DISCOVERY_NOT_FOUND` / `AD_ACCOUNT_NOT_FOUND` 這類是端點在、資源不在 → **不算**。
 *
 * 其餘(401 / 5xx / 網路)一律 false —— 呼叫端自己決定要導 /login 還是 fail-soft。
 */
export function isPlatformUnavailableError(err: unknown): boolean {
  if (!(err instanceof ApiHttpError)) return false;
  const slug = getAdsErrorSlug(err);
  if (err.status === 403 && slug === 'FEATURE_DISABLED') return true;
  if (err.status === 404 && slug === null) return true;
  return false;
}

/** discovery 列是不是「啟用中」(平台相依;非啟用中 → 灰字 + badge,仍可勾)。 */
export function discoveredAccountIsActive(platform: AdsPlatform, status: string): boolean {
  return status === DISCOVERED_ACTIVE_STATUS[platform];
}

/**
 * discovery 列是不是**唯讀**(F109-4):Meta 的 `user_tasks` 不含 ADVERTISE / MANAGE。
 *
 * `userTasks === null`(欄位缺 = 未知)→ **false**:不知道就不標,標成唯讀是替 BE 宣告它
 * 沒說過的事;Google 列永遠 false(Google 線不在這裡表達寫權)。
 */
export function discoveredAccountIsReadOnly(
  platform: AdsPlatform,
  account: Pick<DiscoveredAccount, 'userTasks'>,
): boolean {
  if (platform !== 'meta' || account.userTasks === null) return false;
  return !account.userTasks.some((t) => META_WRITE_TASKS.includes(t));
}

/**
 * 已綁定帳戶是不是**唯讀**(F109-5:藏「上傳到 Meta」入口、畫「唯讀」badge)。
 *
 * 直接讀 BE 推導好的 `write_ok`(`capability.derive_write_ok`),**不用 `user_tasks` 重算**
 * —— 兩套帳。`null`(Google 列 / Meta 欄位缺)= 未知 → false,不畫也不禁用。
 */
export function accountIsReadOnly(account: Pick<AdAccountSummary, 'writeOk'>): boolean {
  return account.writeOk === false;
}

/**
 * 身分還剩幾天到期(F109-6)。`null` = 沒有到期概念(Google;或 Meta 欄位缺 / 格式壞)。
 * 可能是負數(已過期但 BE 還沒 lazy 判死 → 呈現當「已失效」)。
 */
export function identityExpiryDaysLeft(
  identity: Pick<AdsOAuthIdentity, 'expiresAt'>,
  now: number = Date.now(),
): number | null {
  if (!identity.expiresAt) return null;
  const ts = new Date(identity.expiresAt).getTime();
  if (Number.isNaN(ts)) return null;
  return Math.floor((ts - now) / DAY_MS);
}

/**
 * 身分是否「即將到期」:剩 `0 ≤ days < 7`。已過期(負數)不算「即將」—— 那是
 * {@link identityIsExpired} 的事,文案要說「已到期」。
 */
export function identityIsExpiring(
  identity: Pick<AdsOAuthIdentity, 'expiresAt' | 'status'>,
  now: number = Date.now(),
): boolean {
  if (identity.status !== 'ACTIVE') return false;
  const days = identityExpiryDaysLeft(identity, now);
  return days !== null && days >= 0 && days < IDENTITY_EXPIRY_WARN_DAYS;
}

/** 到期時間已過(BE 尚未把 status 轉 `REVOKED_BY_META` 前的空窗)→ 一樣當需重新授權。 */
export function identityIsExpired(
  identity: Pick<AdsOAuthIdentity, 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  const days = identityExpiryDaysLeft(identity, now);
  return days !== null && days < 0;
}

/**
 * 找出「這顆帳戶是哪把身分綁的」。
 *
 * R301.1 — 帳戶列帶 `oauth_user_id`(BE 2026-09-08 additive)時**以 id 為準**:命中就是它、
 * 沒命中就是 `null`(不再退去比 email —— id 說「是 A」而 email 對到 B,信 email 就是把別人的
 * 到期日寫在這顆帳戶上)。舊 BE / legacy 未回填(`null`)→ 退回 R299 的 email 判準:
 * email **非空且恰好命中一把**才算;Meta 的 email 可能為空 → 對不回就 `null`,呈現端不畫到期提示。
 */
export function identityForAccount(
  account: Pick<AdAccountSummary, 'oauthEmail' | 'oauthUserId'>,
  identities: ReadonlyArray<AdsOAuthIdentity>,
): AdsOAuthIdentity | null {
  if (account.oauthUserId) {
    return identities.find((i) => i.oauthUserId === account.oauthUserId) ?? null;
  }
  if (!account.oauthEmail) return null;
  const hits = identities.filter((i) => i.oauthEmail === account.oauthEmail);
  return hits.length === 1 ? (hits[0] ?? null) : null;
}

/**
 * D3 廣告串接 的「連線狀態」欄(列表 / 卡片 / 抽屜三處共用同一個答案)。
 *
 * 優先序(高到低)—— 每一格對應使用者**該做的事**不同:
 *   1. `expired`  授權已失效 → 重新授權(`needsReauth`:`status !== 'ACTIVE'` /
 *      `syncStatus === 'TOKEN_EXPIRED'` / `lastErrorClass === 'OAUTH_EXPIRED'`;Meta 身分
 *      到期日已過也算)
 *   2. `off`      平台端不可用 → 去平台後台處理,**重新授權沒有用**:Meta `account_status`
 *      `DISABLED`(平台端已停用)/ Google 同步狀態 `ACCOUNT_NOT_ENABLED`(帳戶未啟用)/
 *      R301 `last_error_class` `ADS_CUSTOMER_NOT_ACCESSIBLE` · `ADS_PERMISSION_DENIED`(身分活著但
 *      看不到 / 沒權限讀這個帳戶 → `not_accessible`)· `ADS_RESOURCE_MISSING`(帳戶已不存在 → `gone`)
 *   3. `expiring` 授權將到期(Meta 身分剩 <7 天)→ 建議現在重新授權
 *   4. `ok`       已連線
 *
 * ⚠️ 這個 union **不涵蓋同步結果**(OK / FAILED / RUNNING / NEVER):那是「最近一次同步
 * 發生什麼」,與「這條連線還能不能用」是兩個問題(R298 紅線),列表另有 sync badge。
 */
export type ConnectionState =
  | { kind: 'ok' }
  | { kind: 'expiring'; daysLeft: number }
  | { kind: 'expired' }
  | { kind: 'off'; reason: 'platform_disabled' | 'not_enabled' | 'not_accessible' | 'gone' };

export function deriveConnectionState(
  account: Pick<
    AdAccountSummary,
    'platform' | 'status' | 'syncStatus' | 'lastErrorClass' | 'accountStatus'
  >,
  fact: Pick<AccountSyncFact, 'syncStatus'>,
  identity: Pick<AdsOAuthIdentity, 'expiresAt' | 'status'> | null,
  now: number = Date.now(),
): ConnectionState {
  const { needsReauth } = deriveAccountActionState(account);
  // 同步事實(wire 優先 → job 推導 → R297 由 wire 細分)說 TOKEN_EXPIRED 也算:歷史撈不到時
  // 它就是 wire 值,撈得到時是 BE 由 job error_class 細分回寫的 —— 兩者都是授權層的話。
  if (
    needsReauth ||
    // R306 — 同 `deriveAccountActionState`:`TOKEN_TIMEOUT_EXHAUSTED` 說了重授權無效,
    // 這條路徑也要讓開,否則帳戶卡照樣顯「需重新授權」(判準漏一處 = 修了一半)。
    (!reauthRuledOut(account.lastErrorClass) && fact.syncStatus === 'TOKEN_EXPIRED') ||
    (identity !== null && identityIsExpired(identity, now))
  ) {
    return { kind: 'expired' };
  }
  // R301 — 每個綁定各自的授權事實(BE docs/37 §3 / docs/111 §4):身分活著但帳戶端不給看。
  // ⚠️ 不是 `expired`:重新授權對它無效,CTA 要收掉(docs 原話「不顯示重新授權」)。
  if (account.lastErrorClass === 'ADS_RESOURCE_MISSING') {
    return { kind: 'off', reason: 'gone' };
  }
  if (
    account.lastErrorClass === 'ADS_CUSTOMER_NOT_ACCESSIBLE' ||
    account.lastErrorClass === 'ADS_PERMISSION_DENIED'
  ) {
    return { kind: 'off', reason: 'not_accessible' };
  }
  if (account.platform === 'meta' && account.accountStatus === 'DISABLED') {
    return { kind: 'off', reason: 'platform_disabled' };
  }
  // Google 的「未啟用」住在同步狀態上(R296/R297:BE 由 sync job 的 error_class 細分回寫);
  // 也看 wire 自己的 syncStatus —— 歷史撈不到時 fact 只剩 wire 那一半,兩處都認。
  if (fact.syncStatus === 'ACCOUNT_NOT_ENABLED' || account.syncStatus === 'ACCOUNT_NOT_ENABLED') {
    return { kind: 'off', reason: 'not_enabled' };
  }
  if (identity !== null && identityIsExpiring(identity, now)) {
    const daysLeft = identityExpiryDaysLeft(identity, now) ?? 0;
    return { kind: 'expiring', daysLeft };
  }
  return { kind: 'ok' };
}
